import React, { useState, useRef, useEffect, useMemo } from "react";
import CountdownTimer from "../../src/Counter.jsx";
import placeholder from "../assets/Car place holder PNG.png";
import axios from "axios";
import { Link, Bookmark } from "lucide-react";

import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import SingleAuction from "./singleAuction";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
// currency helpers — updated import
import { useCurrencyUtils } from "../utils/currencyUtils";
import api from "../lib/api.ts";
import BidInformation from "./BidInformation";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

const AuctionComponent = () => {
  const { language, translate } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [cardIndex, setCardIndex] = useState(0);
  const [laneData, setLaneData] = useState({});
  const [auctions, setAuctions] = useState([]);
  const [activeLane, setActiveLane] = useState("");
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [liveAuction, setLiveAuction] = useState([]);
  const [AuctionLaneStatus, setAuctionLaneStatus] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState({});

  // currency helpers — now using both convertPrice and displayPriceWithSymbol
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  // Fetch wishlist on component mount to check which vehicles are bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;

        if (wishlistData && Array.isArray(wishlistData)) {
          const bookmarkState = {};
          wishlistData.forEach((item) => {
            bookmarkState[item.vehicleId] = true;
          });
          setIsBookmarked(bookmarkState);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id]);

  const sliderRef = useRef(null);
  const navigate = useNavigate();

  // Prebid functionality state
  const [showBidNowPopup, setShowBidNowPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle bid now button click - show prebid popup
  const handleBidNow = (vehicle) => {
    let auctionLaneData = null;
    if (liveAuction && liveAuction.length > 0) {
      for (const auction of liveAuction) {
        if (auction.auctionLanes && auction.auctionLanes.length > 0) {
          for (const lane of auction.auctionLanes) {
            if (
              lane.auctionLaneVehicles &&
              lane.auctionLaneVehicles.length > 0
            ) {
              const foundVehicle = lane.auctionLaneVehicles.find(
                (av) => av.vehicleId === vehicle.id
              );
              if (foundVehicle) {
                auctionLaneData = {
                  id: foundVehicle.id,
                  auctionLane: {
                    id: lane.id,
                    name: lane.lane?.name,
                  },
                  runNo: foundVehicle.runNo,
                };
                break;
              }
            }
          }
        }
        if (auctionLaneData) break;
      }
    }

    const vehicleWithAuctionData = {
      ...vehicle,
      auctionLanesVehicles: auctionLaneData ? [auctionLaneData] : [],
    };

    console.log("Found auction lane data:", auctionLaneData);
    console.log("Vehicle with auction data:", vehicleWithAuctionData);
    setSelectedVehicle(vehicleWithAuctionData);
    setShowBidNowPopup(true);

    const currentLatestBid = parseFloat(vehicle?.latestBid) || 0;
    const baseAmount =
      currentLatestBid > 0 ? currentLatestBid : vehicle?.startingBid;
    setBidAmount(baseAmount + (vehicle?.bidIncrement || 100));
  };

  // Handle bid now submit
  const handleBidNowSubmit = async () => {
    setIsSubmitting(true);
    try {
      let finalAuctionLaneId =
        selectedVehicle?.auctionLanesVehicles?.[0]?.auctionLane?.id;

      if (
        !finalAuctionLaneId &&
        selectedVehicle?.auctionLanesVehicles?.[0]?.id
      ) {
        finalAuctionLaneId = selectedVehicle.auctionLanesVehicles[0].id;
      }

      if (!finalAuctionLaneId) {
        showError("Auction Lane ID is missing. Cannot submit bid now.");
        return;
      }

      if (!selectedVehicle?.id) {
        showError("Vehicle ID is missing. Cannot submit bid now.");
        return;
      }

      if (!bidAmount || bidAmount <= 0) {
        showError("Please enter a valid bid amount.");
        return;
      }

      const response = await api.post("/bidding/prebid", {
        auctionLaneId: finalAuctionLaneId,
        vehicleId: selectedVehicle.id,
        amount: bidAmount,
      });

      if (response.data.success) {
        setSelectedVehicle((prevVehicle) => {
          const updatedVehicle = {
            ...prevVehicle,
            latestBid: bidAmount,
            highestBidderId: user?.id,
          };
          console.log("Updated selectedVehicle with new bid:", updatedVehicle);
          return updatedVehicle;
        });

        setLiveAuction((prevAuctions) =>
          prevAuctions.map((auction) => ({
            ...auction,
            auctionLanes: auction.auctionLanes?.map((lane) => ({
              ...lane,
              auctionLaneVehicles: lane.auctionLaneVehicles?.map((av) =>
                av.vehicleId === selectedVehicle.id
                  ? {
                    ...av,
                    vehicle: {
                      ...av.vehicle,
                      latestBid: bidAmount,
                      highestBidderId: user?.id,
                    },
                  }
                  : av
              ),
            })),
          }))
        );

        setLaneData((prevLaneData) => {
          const updatedLaneData = { ...prevLaneData };
          Object.keys(updatedLaneData).forEach((laneName) => {
            updatedLaneData[laneName] = updatedLaneData[laneName].map(
              (vehicle) =>
                vehicle.vehicleId === selectedVehicle.id
                  ? {
                    ...vehicle,
                    vehicle: {
                      ...vehicle.vehicle,
                      latestBid: bidAmount,
                      highestBidderId: user?.id,
                    },
                  }
                  : vehicle
            );
          });
          console.log("Updated laneData with new bid:", updatedLaneData);
          return updatedLaneData;
        });

        const currentBid = parseFloat(bidAmount);
        const increment = parseFloat(selectedVehicle?.bidIncrement) || 100;
        const nextBidAmount = currentBid + increment;
        setBidAmount(nextBidAmount.toString());

        showSuccess("Bid now submitted successfully!");
      } else {
        showError(
          response.data.message || "Failed to submit bid now. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting bid now:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit bid now. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeBidNowPopup = () => {
    setShowBidNowPopup(false);
    setSelectedVehicle(null);
  };

  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        const wasBookmarked = isBookmarked[vehicleId] || false;
        setIsBookmarked((prev) => ({
          ...prev,
          [vehicleId]: !wasBookmarked,
        }));

        if (wasBookmarked) {
          showSuccess("Vehicle removed from Watch List successfully!");
        } else {
          showSuccess("Vehicle added to Watch List successfully!");
        }
      } else {
        showError("Failed to update Watch List. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      showError("Failed to update Watch List. Please try again.");
    }
  };

  const toDubaiDateStr = (dateStr) => {
    if (!dateStr) return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    try {
      return dayjs(dateStr).tz(DUBAI_TZ).format("YYYY-MM-DD");
    } catch (error) {
      console.warn("Error formatting date:", dateStr, error);
      return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    }
  };

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const res2 = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              page: 1,
              limit: 50,
              orderBy: "startDate",
              order: "ASC",
              search: "",
              slug: "auctions",
              type: "live",
              filters: {
                unSoldVehicles: 1,
              },
            },
          }
        );

        console.log("res2", res2);

        const LiveAuction = res2.data.data || [];
        setLiveAuction(LiveAuction);

        const laneStatusMap = {};
        LiveAuction.forEach((auction) => {
          if (auction.auctionLanes) {
            laneStatusMap[auction.id] = auction.auctionLanes.map((lane) => ({
              laneId: lane.id || lane.laneId,
              status:
                typeof lane.auctionStatus === "string"
                  ? lane.auctionStatus
                  : lane.auctionStatus?.name ||
                  lane.auctionStatus?.slug ||
                  lane.status,
            }));
          }
        });
        setAuctionLaneStatus(laneStatusMap);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              type: "upcomming,live",
              orderBy: "startDate",
              order: "ASC",
              filters: { isActive: true },
              limit: 1,
              filters: {
                unSoldVehicles: 1,
              },
            },
          }
        );

        const allAuctions = [...LiveAuction, ...res.data.data];

        const mapped = allAuctions.map((a) => {
          if (!a || typeof a !== "object") {
            console.warn("Invalid auction data:", a);
            return null;
          }

          let auctionName = `Auction ${a.id}`;
          if (typeof a.name === "string" && a.name.trim()) {
            auctionName = a.name.trim();
          } else if (typeof a.auction === "string" && a.auction.trim()) {
            auctionName = a.auction.trim();
          }

          const auctionLanes =
            a.auctionLanes
              ?.map((lane, idx) => {
                if (!lane || typeof lane !== "object") {
                  console.warn("Invalid lane data:", lane);
                  return null;
                }

                const vehicles = lane.auctionLaneVehicles || [];
                const laneName = lane.lane?.name || lane.name;

                const mappedStatus = AuctionLaneStatus[a.id]?.find(
                  (status) => status.laneId === (lane.id || lane.laneId)
                )?.status;

                const laneStatus =
                  mappedStatus ||
                  (typeof lane.auctionStatus === "string"
                    ? lane.auctionStatus
                    : lane.auctionStatus?.name || lane.auctionStatus?.slug) ||
                  (typeof lane.status === "string"
                    ? lane.status
                    : lane.status?.name || lane.status?.slug) ||
                  "unknown";

                return {
                  id:
                    typeof lane.id === "number" || typeof lane.id === "string"
                      ? lane.id
                      : idx,
                  name:
                    typeof laneName === "string" ? laneName : `Lane ${idx + 1}`,
                  vehicles,
                  status: laneStatus,
                  totalVehicles: Array.isArray(vehicles) ? vehicles.length : 0,
                };
              })
              .filter((lane) => lane !== null) || [];

          const totalCars = auctionLanes.reduce(
            (sum, lane) =>
              sum +
              (typeof lane?.totalVehicles === "number"
                ? lane.totalVehicles
                : 0),
            0
          );

          return {
            id: a.id,
            auction: auctionName,
            name: a.name,
            description: a.description,
            date: toDubaiDateStr(a.startDate),
            startDate: a.startDate || new Date().toISOString(),
            branch: a.branch || null,
            status: a.status,
            type: a.type,
            auctionLanes,
            totalCars,
          };
        });

        const validMapped = mapped.filter(
          (auction) => auction !== null && auction.totalCars > 0
        );

        const validatedMapped = validMapped.map((auction) => {
          if (typeof auction.auction !== "string") {
            console.warn(
              "Invalid auction name type:",
              typeof auction.auction,
              auction.auction
            );
            return {
              ...auction,
              auction: `Auction ${auction.id}`,
            };
          }
          if (typeof auction.totalCars !== "number") {
            console.warn(
              "Invalid totalCars type:",
              typeof auction.totalCars,
              auction.totalCars
            );
            return {
              ...auction,
              totalCars: 0,
            };
          }
          return auction;
        });

        setAuctions(validatedMapped);

        const now = dayjs().tz(DUBAI_TZ);
        let nextAuction = null;
        let minDiff = Infinity;

        validatedMapped.forEach((a) => {
          if (!a.startDate) return;
          try {
            const auctionTime = dayjs.utc(a.startDate).tz(DUBAI_TZ);
            const diff = auctionTime.diff(now);
            if (diff >= 0 && diff < minDiff) {
              minDiff = diff;
              nextAuction = a;
            } else if (nextAuction === null && diff < 0) {
              const pastDiff = Math.abs(diff);
              if (pastDiff < minDiff) {
                minDiff = pastDiff;
                nextAuction = a;
              }
            }
          } catch (error) {
            console.warn("Error processing auction time:", a.startDate, error);
          }
        });

        setSelectedAuction(nextAuction);
      } catch (err) {
        console.error("Error fetching auctions", err);
        setLaneData({});
        setActiveLane("");
      }
    };
    fetchAuctions();
  }, []);

  const scrollLeft = () => {
    if (cardIndex > 0) {
      setCardIndex((prev) => prev - 1);
      sliderRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const maxIndex = (laneData[activeLane]?.length || 0) - 1;
    if (cardIndex < maxIndex) {
      setCardIndex((prev) => prev + 1);
      sliderRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const updateLanesForSelectedAuction = (auction) => {
    if (!auction || !auction.auctionLanes) {
      setLaneData({});
      setActiveLane("");
      return;
    }

    console.log(
      "Updating lanes for auction:",
      auction.auction || auction.name,
      "Lanes:",
      auction.auctionLanes
    );

    const lanes = {};
    const laneList = Array.isArray(auction.auctionLanes)
      ? [...auction.auctionLanes]
      : [];

    laneList
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .forEach((laneObj) => {
        const laneName = laneObj.name || `Lane ${laneObj.id}`;
        const vehicles = (laneObj.vehicles || []).map((vehicle) => ({
          ...vehicle,
          startDate: auction.startDate,
        }));
        lanes[laneName] = vehicles;
        console.log(`Lane ${laneName}: ${vehicles.length} vehicles`);
      });

    const sortedNames = Object.keys(lanes).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
    const orderedLanes = {};
    sortedNames.forEach((name) => {
      orderedLanes[name] = lanes[name];
    });

    console.log("Final lane data:", orderedLanes);
    setLaneData(orderedLanes);
    setActiveLane(sortedNames[0] || "");
    setCardIndex(0);
  };

  useEffect(() => {
    if (selectedAuction) {
      updateLanesForSelectedAuction(selectedAuction);
    }
  }, [selectedAuction]);

  const calculateCountdown = (endDate) => {
    if (!endDate) return null;
    try {
      const end = new Date(endDate);
      const now = new Date();
      const diff = end.getTime() - now.getTime();

      if (diff <= 0) return null;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return {
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      };
    } catch (error) {
      return null;
    }
  };

  const [countdowns, setCountdowns] = useState({});

  useEffect(() => {
    if (!selectedAuction || !activeLane || !laneData[activeLane]) return;

    const interval = setInterval(() => {
      const timers = {};
      laneData[activeLane].forEach((car) => {
        const endDate = car.startDate || selectedAuction.endDate || selectedAuction.startDate;
        const timer = calculateCountdown(endDate);
        if (timer) {
          timers[car.id] = timer;
        }
      });
      setCountdowns(timers);
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedAuction, activeLane, laneData]);

  if (!auctions || auctions.length === 0) {
    return null;
  }

  return (
    <>
      <div className='bg-white px-24 py-12 max-w-full mx-auto font-Poppins'>
        {/* Header Section */}
        <div className='flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8'>
          <div>
            <h2 className='text-3xl md:text-4xl font-bold text-primary mb-2'>
              {translate("liveAuctions.title") || "Live Auctions"}
            </h2>
            <p className='text-primary-light text-sm md:text-base'>
              {translate("liveAuctions.description") || "Bidding is active on these premium vehicles"}
            </p>
          </div>
          <a
            href={selectedAuction ? `/vehicles?auctionId=${selectedAuction?.id}` : '/vehicles'}
            className='text-primary hover:text-primary-dark font-semibold text-sm md:text-base flex items-center gap-1'
          >
            {translate("buttons.viewAll") || "View All"}
            <span>&gt;</span>
          </a>
        </div>

        {/* Lane Tabs */}
        {Object.keys(laneData).length > 1 && (
          <div className='mb-6 hidden md:flex gap-2'>
            {Object.keys(laneData).map((lane) => (
              <button
                key={lane}
                onClick={() => {
                  setActiveLane(lane);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${lane === activeLane
                    ? 'bg-[#E95923] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {lane}
              </button>
            ))}
          </div>
        )}

        {/* Cards Grid */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
          {(laneData[activeLane] || []).length === 0 ? (
            <div className='col-span-full text-center py-12 text-description'>
              {translate("auctionComponent.noVehicles")}
            </div>
          ) : (
            (laneData[activeLane] || [])
              .slice()
              .sort((a, b) => (a.runNo ?? 0) - (b.runNo ?? 0))
              .map((car) => {
                const timer = countdowns[car.id];
                const vehicleRating = car.vehicle?.rating || 4.5;

                return (
                  <div
                    key={car.id}
                    className='bg-[#1a1a1a] rounded-lg overflow-hidden flex flex-col'
                  >
                    <div className='relative h-48 bg-gray-800'>
                      <img
                        src={
                          car.vehicle?.imageUrl ||
                          (Array.isArray(car.vehicle?.images) &&
                            car.vehicle.images.length > 0
                            ? car.vehicle.images[0].imageUrl
                            : placeholder)
                        }
                        alt={car.vehicle?.name}
                        className='w-full h-full object-cover'
                      />

                      <div className='absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1'>
                        <span className='w-1.5 h-1.5 bg-white rounded-full'></span>
                        LIVE
                      </div>

                      {timer && (
                        <div className='absolute bottom-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1'>
                          <svg
                            width='12'
                            height='12'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <circle cx='12' cy='12' r='10' stroke='white' strokeWidth='2' />
                            <path d='M12 6V12L16 14' stroke='white' strokeWidth='2' strokeLinecap='round' />
                          </svg>
                          {timer.hours}:{timer.minutes}:{timer.seconds}
                        </div>
                      )}
                    </div>

                    <div className='p-4 flex-1 flex flex-col'>
                      <div className='flex justify-between items-start mb-2'>
                        <h3 className='text-white font-bold text-base flex-1'>
                          {car.vehicle?.name}
                        </h3>
                        <div className='flex items-center gap-1 text-yellow-400 ml-2'>
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='currentColor'
                          >
                            <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
                          </svg>
                          <span className='text-sm font-semibold'>{vehicleRating.toFixed(1)}</span>
                        </div>
                      </div>

                      <p className='text-gray-400 text-sm mb-4'>
                        {car.vehicle?.mileage ? `${car.vehicle.mileage} KM` : ''} • {car.vehicle?.transmissionType || 'Automatic'}
                      </p>

                      <div className='mt-auto'>
                        <p className='text-gray-400 text-xs mb-1'>CURRENT BID</p>
                        <p className='text-white font-bold text-2xl mb-4'>
                          {displayPriceWithSymbol(convertPrice(Number(car?.vehicle?.latestBid || car?.vehicle?.startingBid || 0)))}
                        </p>

                        <button
                          onClick={() => handleBidNow(car.vehicle)}
                          className='w-full bg-primary hover:bg-primary-dark text-white font-semibold py-2.5 rounded transition-colors'
                        >
                          Bid
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>

      {/* Prebid Popup Modal */}
      {showBidNowPopup && selectedVehicle && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-bold text-gray-900'>
                Bid Now Summary
              </h3>
              <button
                onClick={closeBidNowPopup}
                className='text-gray-400 hover:text-gray-600'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='mb-4'>
              <h4 className='font-semibold text-gray-900 mb-2'>
                {selectedVehicle.name}
              </h4>
              <div className='flex justify-between text-sm text-gray-600'>
                <span>
                  Lot No:{" "}
                  <span className='font-semibold text-primary'>
                    {selectedVehicle.lotNumber}
                  </span>
                </span>
                {selectedVehicle.auctionLanesVehicles?.[0]?.runNo && (
                  <span>
                    Run No:{" "}
                    <span className='font-semibold text-primary'>
                      {selectedVehicle.auctionLanesVehicles[0].runNo}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {(() => {
              console.log(
                "Rendering BidInformation with vehicle:",
                selectedVehicle
              );
              console.log("Vehicle latestBid:", selectedVehicle?.latestBid);
              return (
                <BidInformation
                  key={`${selectedVehicle.id}-${selectedVehicle.latestBid}`}
                  vehicle={selectedVehicle}
                  prebidTimeLeft={0}
                  bidAmount={bidAmount}
                  setBidAmount={setBidAmount}
                  bidIncrement={selectedVehicle?.bidIncrement || 100}
                  isSubmittingBid={isSubmitting}
                  handlePreBidSubmit={handleBidNowSubmit}
                  getMinBidAmount={() => {
                    const currentLatestBid =
                      parseFloat(selectedVehicle?.latestBid) || 0;
                    return currentLatestBid > 0
                      ? currentLatestBid +
                      (selectedVehicle?.bidIncrement || 100)
                      : selectedVehicle?.startingBid;
                  }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default AuctionComponent;